import { css } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo } from "react";

/**
 * Side-by-side media treatment — two photos in a static horizontal row.
 * Same fixed height as MarqueeMedia; each image keeps its intrinsic width
 * (no crop-to-fill). Small gap between photos. Design theme does not style
 * these images; layout CSS only.
 *
 * data-cy leftImage / rightImage match EditableHeader's modular pencil hooks.
 */
/** Shared visual height with MarqueeMedia / SlideshowMedia. */
const IMAGE_HEIGHT = styleUtils.rem(520);

const styles = {
  // Height is locked; width: auto keeps natural proportions (no crop).
  image: css`
    display: block;
    flex-shrink: 0;
    height: ${IMAGE_HEIGHT};
    width: auto;
  `,
  row: css`
    align-items: stretch;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing.x2};
    width: max-content;
  `,
  viewport: css`
    display: flex;
    justify-content: center;
    margin-bottom: ${styleUtils.rem(30)};
    overflow: hidden;
    width: 100%;
  `,
};

export type SideBySideMediaProps = {
  alt: string;
  leftPhotoURL: string;
  rightPhotoURL: string;
};

const SideBySideMedia = ({
  alt,
  leftPhotoURL,
  rightPhotoURL,
}: SideBySideMediaProps) => (
  <div css={styles.viewport} data-cy="sideBySideMedia">
    <div css={styles.row}>
      <img
        alt={alt}
        css={styles.image}
        data-cy="leftImage"
        src={leftPhotoURL}
      />
      <img
        alt={alt}
        css={styles.image}
        data-cy="rightImage"
        src={rightPhotoURL}
      />
    </div>
  </div>
);

const MemoizedSideBySideMedia = memo(SideBySideMedia);

export { MemoizedSideBySideMedia as SideBySideMedia };
